import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import { Shield, CheckCircle, Mail, Phone, FileText, Eye, Search, Calendar, Users, X, Loader2, PauseCircle, PlayCircle, TrendingUp, Award, Download, FileDown, SlidersHorizontal, MapPin, Layers, Clock } from 'lucide-react';
import OscopLogo from '../brand/OscopLogo';
import { detectTerritoire, detectPlan, ageInDays, ANCIENNETE_LABELS, getInitials, anciennetéBucket } from './gcaConstants';
import { useGestionCoopers } from './useGestionCoopers';
import GestionCoopersModals from './GestionCoopersModals';

export default function GestionCoopersAmeliore() {
  const ctx = useGestionCoopers();
  const { ancienneteFilter, availablePlans, availableTerritoires, calculatePerformanceScore, coopers, exportCSV, exportPDF, filteredCoopers, handleOpenActionDialog, handleOpenDetailModal, hasActiveFilter, loading, planFilter, resetFilters, searchQuery, setAncienneteFilter, setPlanFilter, setSearchQuery, setShowAdvanced, setSortOrder, setStatusFilter, setTerritoireFilter, showAdvanced, sortOrder, statusFilter, territoireFilter } = ctx;
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="coopers-loading">
        <Loader2 className="h-10 w-10 animate-spin text-[#DEA900]" />
      </div>
    );
  }

  const stats = {
    validated: coopers.filter(c => c.is_validated && c.is_active).length,
    pending: coopers.filter(c => !c.is_validated).length,
    suspended: coopers.filter(c => !c.is_active).length,
    total: coopers.length,
  };

  const StatusBadge = ({ cooper }) => {
    if (!cooper.is_active) return <Badge className="bg-rose-600 hover:bg-rose-600 text-white font-semibold"><PauseCircle className="h-3 w-3 mr-1" />Suspendu</Badge>;
    if (!cooper.is_validated) return <Badge className="bg-[#DEA900] hover:bg-[#DEA900] text-[#0a3d62] font-semibold"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    return <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-semibold"><CheckCircle className="h-3 w-3 mr-1" />Validé</Badge>;
  };

  return (
    <div data-testid="coopers-section-premium">
      {/* ============ STATS PREMIUM ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { key: 'validated', label: "COOPER'S Validés", value: stats.validated, accent: 'emerald', Icon: Shield },
          { key: 'pending', label: 'En Attente', value: stats.pending, accent: 'gold', Icon: Clock },
          { key: 'suspended', label: 'Suspendus', value: stats.suspended, accent: 'rose', Icon: PauseCircle },
          { key: 'total', label: 'Total', value: stats.total, accent: 'navy', Icon: Users },
        ].map(({ key, label, value, accent, Icon }) => {
          const palette = {
            emerald: { bar: 'bg-emerald-500', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700' },
            gold: { bar: 'bg-[#DEA900]', iconBg: 'bg-[#DEA900]/12', iconColor: 'text-[#b88600]', valueColor: 'text-[#b88600]' },
            rose: { bar: 'bg-rose-500', iconBg: 'bg-rose-50', iconColor: 'text-rose-600', valueColor: 'text-rose-700' },
            navy: { bar: 'bg-[#0a3d62]', iconBg: 'bg-[#0a3d62]/8', iconColor: 'text-[#0a3d62]', valueColor: 'text-[#0a3d62]' },
          }[accent];
          return (
            <div
              key={key}
              data-testid={`coopers-stat-${key}`}
              className="group relative bg-white border border-slate-200/80 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)]"
            >
              <span className={`absolute left-0 top-5 bottom-5 w-1 rounded-r-full ${palette.bar}`} />
              <div className="flex items-center justify-between pl-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className={`text-4xl font-bold tracking-tight tabular-nums mt-1 ${palette.valueColor}`}>{value}</p>
                </div>
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${palette.iconBg} transition-transform group-hover:scale-105`}>
                  <Icon className={`h-6 w-6 ${palette.iconColor}`} strokeWidth={2.2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ============ HEADER + FILTRES PREMIUM ============ */}
      <div className="bg-white rounded-2xl border border-slate-200/80 mb-6 overflow-hidden" data-testid="coopers-filters">
        {/* Bandeau navy */}
        <div className="relative bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
          <div className="relative flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30">
              <OscopLogo size={28} variant="on-navy" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg leading-none">Gestion des COOPER'S</h3>
              <p className="text-[#f5cc3e] text-[11px] uppercase tracking-wider mt-1">Réseau partenaire ESS</p>
            </div>
          </div>
          <div className="relative flex flex-wrap gap-2">
            <Button
              onClick={exportCSV}
              size="sm"
              variant="outline"
              className="bg-white/10 hover:bg-white/20 border-white/25 text-white"
              data-testid="export-csv-btn"
              disabled={filteredCoopers.length === 0}
            >
              <FileDown className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
            <Button
              onClick={exportPDF}
              size="sm"
              className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold border-0"
              data-testid="export-pdf-btn"
              disabled={filteredCoopers.length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Recherche + statut */}
        <div className="p-5 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher par entreprise, SIRET, email, nom…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:border-[#DEA900] focus:ring-[#DEA900]/20"
                data-testid="coopers-search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[170px] bg-slate-50 border-slate-200" data-testid="coopers-status-filter">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="validated"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" />Validés</span></SelectItem>
                  <SelectItem value="pending"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#DEA900]" />En attente</span></SelectItem>
                  <SelectItem value="suspended"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-rose-500" />Suspendus</span></SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(p => p === 'desc' ? 'asc' : 'desc')}
                className="border-slate-200"
                data-testid="coopers-sort-btn"
              >
                <Calendar className="h-4 w-4 mr-1.5" />
                {sortOrder === 'desc' ? 'Plus récent' : 'Plus ancien'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdvanced(s => !s)}
                className={`border-slate-200 ${showAdvanced ? 'bg-[#0a3d62] text-white border-[#0a3d62] hover:bg-[#0c4a78] hover:text-white' : ''}`}
                data-testid="coopers-advanced-toggle"
              >
                <SlidersHorizontal className="h-4 w-4 mr-1.5" />
                Filtres avancés
              </Button>
            </div>
          </div>

          {/* FILTRES AVANCÉS */}
          {showAdvanced && (
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-gradient-to-br from-[#0a3d62]/3 to-[#DEA900]/3 border border-slate-200 rounded-xl"
              data-testid="coopers-advanced-filters"
            >
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#0a3d62] font-semibold flex items-center gap-1.5 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 text-[#DEA900]" /> Territoire
                </Label>
                <Select value={territoireFilter} onValueChange={setTerritoireFilter}>
                  <SelectTrigger className="bg-white" data-testid="coopers-territoire-filter">
                    <SelectValue placeholder="Tous les territoires" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les territoires</SelectItem>
                    {availableTerritoires.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#0a3d62] font-semibold flex items-center gap-1.5 mb-1.5">
                  <Layers className="h-3.5 w-3.5 text-[#DEA900]" /> Plan
                </Label>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="bg-white" data-testid="coopers-plan-filter">
                    <SelectValue placeholder="Tous les plans" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les plans</SelectItem>
                    {availablePlans.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#0a3d62] font-semibold flex items-center gap-1.5 mb-1.5">
                  <Clock className="h-3.5 w-3.5 text-[#DEA900]" /> Ancienneté
                </Label>
                <Select value={ancienneteFilter} onValueChange={setAncienneteFilter}>
                  <SelectTrigger className="bg-white" data-testid="coopers-anciennete-filter">
                    <SelectValue placeholder="Toutes les anciennetés" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les anciennetés</SelectItem>
                    {Object.entries(ANCIENNETE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${v.dot}`} />{v.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Compteur + reset */}
          <div className="flex items-center justify-between text-sm pt-1">
            <p className="text-slate-700">
              <strong className="text-[#0a3d62] tabular-nums">{filteredCoopers.length}</strong>
              <span className="text-slate-500"> / {coopers.length} COOPER'S</span>
              {searchQuery && <span className="text-slate-500"> · « {searchQuery} »</span>}
            </p>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="text-[#0a3d62] hover:bg-[#0a3d62]/5"
                data-testid="coopers-reset-filters-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ============ LISTE PREMIUM CARDS ============ */}
      {filteredCoopers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center" data-testid="coopers-empty">
          <div className="h-16 w-16 mx-auto rounded-2xl bg-[#0a3d62]/5 flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-[#0a3d62]/40" />
          </div>
          <p className="text-slate-700 font-semibold">
            {hasActiveFilter ? 'Aucun COOPER\'S ne correspond aux filtres' : 'Aucun COOPER\'S enregistré'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {hasActiveFilter ? 'Ajustez vos filtres ou réinitialisez pour voir tous les partenaires.' : 'Les nouveaux partenaires apparaîtront ici.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="coopers-list">
          {filteredCoopers.map((cooper) => {
            const perfScore = calculatePerformanceScore(cooper);
            const initials = getInitials(cooper.entreprise);
            const territoire = detectTerritoire(cooper);
            const plan = detectPlan(cooper);
            const ageDays = ageInDays(cooper.created_at);
            const bucket = anciennetéBucket(ageDays);
            const ancMeta = ANCIENNETE_LABELS[bucket];
            const statusBorder = !cooper.is_active
              ? 'border-l-rose-500'
              : !cooper.is_validated
                ? 'border-l-[#DEA900]'
                : 'border-l-emerald-500';
            return (
              <div
                key={cooper.id}
                className={`group bg-white rounded-2xl border border-slate-200/80 border-l-4 ${statusBorder} p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-15px_rgba(10,61,98,0.22)] hover:border-l-[#DEA900]`}
                data-testid={`cooper-card-${cooper.id}`}
              >
                {/* HEADER : avatar + identité + statut + perf */}
                <div className="flex items-start gap-3 mb-4">
                  {/* Avatar gradient or */}
                  <div className="relative shrink-0">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#DEA900] to-[#b88600] flex items-center justify-center text-[#0a3d62] font-bold text-lg shadow-md shadow-[#DEA900]/25 ring-1 ring-[#f5cc3e]/40">
                      {initials}
                    </div>
                    {cooper.is_validated && cooper.is_active && (
                      <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                  {/* Identité */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-bold text-[#0a3d62] truncate text-base leading-tight">
                          {cooper.entreprise || 'Sans nom'}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge cooper={cooper} />
                          {cooper.ref_ess && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              <Award className="h-2.5 w-2.5" /> {cooper.ref_ess}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Perf score */}
                      <div className="shrink-0 text-right">
                        <div className={`text-2xl font-bold tabular-nums ${
                          perfScore >= 70 ? 'text-emerald-600' :
                          perfScore >= 40 ? 'text-[#DEA900]' :
                          'text-rose-500'
                        }`}>{perfScore}%</div>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">Performance</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* META : SIRET, Email, Tél, Territoire, Plan, Ancienneté */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4 text-xs">
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <FileText className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="font-mono truncate" title={cooper.siret}>{cooper.siret || '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <MapPin className="h-3 w-3 text-[#DEA900] shrink-0" />
                    <span className="truncate">{territoire}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <Mail className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="truncate" title={cooper.user?.email}>{cooper.user?.email || '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <Layers className="h-3 w-3 text-[#DEA900] shrink-0" />
                    <span className="truncate">{plan}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <Phone className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="truncate">{cooper.user?.telephone || '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                    <ancMeta.icon className="h-3 w-3 text-[#DEA900] shrink-0" />
                    <span className="truncate">{ancMeta.label}</span>
                  </div>
                </div>

                {/* KPIs : Membres / Dossiers / Réussite */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-center group-hover:bg-[#0a3d62]/5 transition-colors">
                    <Users className="h-3.5 w-3.5 mx-auto text-[#0a3d62] mb-1" />
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Membres</p>
                    <p className="font-bold text-sm text-[#0a3d62] tabular-nums">{cooper.membres_assignes || 0}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-center group-hover:bg-[#DEA900]/8 transition-colors">
                    <FileText className="h-3.5 w-3.5 mx-auto text-[#b88600] mb-1" />
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Dossiers</p>
                    <p className="font-bold text-sm text-[#b88600] tabular-nums">{cooper.dossiers_traites || 0}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-center group-hover:bg-emerald-50 transition-colors">
                    <TrendingUp className="h-3.5 w-3.5 mx-auto text-emerald-600 mb-1" />
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Réussite</p>
                    <p className="font-bold text-sm text-emerald-700 tabular-nums">{cooper.taux_reussite || 0}%</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => handleOpenDetailModal(cooper)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-slate-200 hover:border-[#0a3d62] hover:bg-[#0a3d62] hover:text-white"
                    data-testid={`cooper-details-${cooper.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" /> Détails
                  </Button>
                  {!cooper.is_validated && (
                    <Button
                      onClick={() => handleOpenActionDialog(cooper, 'validate')}
                      size="sm"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`cooper-validate-${cooper.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Valider
                    </Button>
                  )}
                  {cooper.is_validated && cooper.is_active && (
                    <Button
                      onClick={() => handleOpenActionDialog(cooper, 'suspend')}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      data-testid={`cooper-suspend-${cooper.id}`}
                    >
                      <PauseCircle className="h-4 w-4 mr-1" /> Suspendre
                    </Button>
                  )}
                  {!cooper.is_active && (
                    <Button
                      onClick={() => handleOpenActionDialog(cooper, 'activate')}
                      size="sm"
                      className="flex-1 bg-[#0a3d62] hover:bg-[#0c4a78] text-white"
                      data-testid={`cooper-activate-${cooper.id}`}
                    >
                      <PlayCircle className="h-4 w-4 mr-1" /> Réactiver
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <GestionCoopersModals ctx={ctx} />
    </div>
  );
}
